<template>
	<v-sheet id="address-lookup-card" density="compact" class="m-2 w-100" rounded="xl" style="max-height: 90dvh; overflow-y: auto">
		<!-- Location loading screen -->
		<div v-if="isLoadingLocation" class="address-lookup-loading text-center pa-6">
			<v-progress-circular indeterminate color="primary" size="40" class="mb-3" />
			<div class="text-body-1">Getting your location…</div>

			<!-- Revealed after 3.5s of waiting (showSlowEscape). On iOS, getCurrentPosition can hang to
			     its timeout (e.g. Location Services disabled mid-session), so this Cancel lets the user
			     bail instead of waiting. JS-timer driven for reliability on iOS WebKit. -->
			<div v-if="showSlowEscape" class="address-lookup-slow-escape mt-4">
				<div class="text-body-2 mb-2" style="color: rgba(0, 0, 0, 0.6)">This is taking longer than expected.</div>
				<v-btn class="address-lookup-slow-cancel" variant="outlined" rounded="xl" size="small" @click="cancelLocationRequest(true)">
					Cancel
				</v-btn>
			</div>
		</div>

		<v-row v-else class="ma-0 no-gutters address-lookup-content">
			<v-col :cols="isMobileLandscape ? 6 : 12" :class="isMobileLandscape ? 'mb-n4' : ''">
				<PermissionHelp :show-permission-help="showPermissionHelp" :error="error" @cancel="goBack" @try-again="handleTryAgain" />

				<div
					v-if="!showPermissionHelp && !error"
					id="map-container"
					class="mb-2 address-lookup-map-container"
					:style="mapContainerStyle">
					<div ref="mapElement" class="address-lookup-map"></div>

					<v-btn
						rounded="xl"
						icon="icon-arrow-left"
						v-if="!currentGoogleLocationAddress"
						tabindex="-1"
						class="map-btn-back"
						@click="goBack">
					</v-btn>

					<v-btn
						icon="icon-refresh"
						rounded="xl"
						tabindex="-1"
						class="map-btn-refresh"
						:loading="isRefreshing"
						@click="refreshLocation">
					</v-btn>
				</div>

				<div v-if="isRefreshing && showSlowEscape" class="address-lookup-slow-escape text-center mt-2">
					<div class="text-body-2 mb-2" style="color: rgba(0, 0, 0, 0.6)">This is taking longer than expected.</div>
					<v-btn class="address-lookup-slow-cancel" variant="outlined" rounded="xl" size="small" @click="cancelLocationRequest()">
						Cancel
					</v-btn>
				</div>
			</v-col>
			<v-col
				:cols="isMobileLandscape ? 6 : 12"
				:class="[isMobileLandscape ? 'py-0 pl-3' : 'pa-3 pt-0', 'address-lookup-results-column']">
				<div v-if="currentGoogleLocationAddress && createNewProject">
					<div class="text-h6 mb-4">Create Project</div>

					<v-form ref="form" v-model="isValid" :readonly="isCreatingProject" @submit.prevent="submitForm">
						<v-row class="mb-0">
							<v-col cols="12">
								<CharacterLimitedTextField
									v-model.trim="formData.name"
									field-noun="project name"
									:max-length="255"
									:rules="nameRules"
									required
									placeholder="Enter project name"
									clearable
									class="mb-4"
									:error-messages="fieldErrors.name">
									<template #label><FieldLabel label="Project Name (Required)" /></template>
								</CharacterLimitedTextField>
								<template v-if="isTouchInput">
									<CharacterLimitedTextField
										field-noun="project address"
										:max-length="255"
										:rules="addressRules"
										required
										placeholder="Search for an address"
										clearable
										:loading="predictions.isLoading.value || predictions.isGeocodingManual.value"
										:model-value="predictions.searchTerm.value"
										:error-messages="fieldErrors.address"
										@update:model-value="onTouchSearchInput"
										@blur="onTouchSearchBlur">
										<template #label><FieldLabel label="Project Address (Required)" /></template>
									</CharacterLimitedTextField>
									<div
										v-if="isTouchInput && showTouchPredictions && predictions.searchTerm.value"
										ref="touchPredictionsListRef"
										class="address-lookup-results-list mb-4">
										<v-list density="compact">
											<v-list-item v-if="predictions.isLoading.value" class="pl-2">
												<v-list-item-title class="text-medium-emphasis">Loading addresses...</v-list-item-title>
											</v-list-item>
											<template v-else-if="predictions.items.value.length > 0 || predictions.searchTerm.value.trim()">
												<v-list-item
													v-for="(item, index) in predictions.items.value"
													:key="item.placeId || item.formattedAddress || index"
													data-test-id="address-prediction-item"
													density="compact"
													class="pa-1"
													@mousedown.prevent
													@click="selectPrediction(item)">
													<v-list-item-title>{{ item.formattedAddress }}</v-list-item-title>
												</v-list-item>
												<v-list-item
													v-if="predictions.searchTerm.value.trim()"
													data-test-id="address-create-new"
													class="pa-1"
													@mousedown.prevent
													@click="createManualAddress(predictions.searchTerm.value)">
													<v-list-item-title class="text-truncate">
														Create new address '{{ predictions.searchTerm.value }}'
													</v-list-item-title>
												</v-list-item>
											</template>
											<v-list-item v-else class="pl-2">
												<v-list-item-title>No addresses found.</v-list-item-title>
											</v-list-item>
										</v-list>
									</div>
								</template>
								<template v-else>
									<CharacterLimitedTextField
										v-model.trim="formData.address"
										field-noun="project address"
										:max-length="255"
										:rules="addressRules"
										required
										placeholder="Enter project name"
										clearable
										:error-messages="fieldErrors.address">
										<template #label><FieldLabel label="Project Address (Required)" /></template>
									</CharacterLimitedTextField>
								</template>
							</v-col>
						</v-row>
						<v-row class="mb-1 mt-0">
							<v-col cols="6">
								<v-btn
									rounded="xl"
									variant="outlined"
									text
									block
									elevation="0"
									class="white-bg"
									:disabled="isCreatingProject"
									@click="clearForm">
									Cancel
								</v-btn>
							</v-col>
							<v-col cols="6">
								<v-btn
									color="primary"
									rounded="xl"
									block
									elevation="0"
									:disabled="isCreatingProject"
									:loading="isCreatingProject"
									@click="submitForm">
									{{ isCreatingProject ? "Creating..." : "Create Project" }}
								</v-btn>
							</v-col>
						</v-row>
					</v-form>
				</div>

				<div v-else>
					<v-container fluid class="px-0">
						<v-row no-gutters>
							<v-col class="mb-4">
								<template v-if="!isTouchInput">
									<GoogleMapsAutocomplete
										id="address-lookup-autocomplete"
										v-model="geoAddress"
										v-test="'project-address'"
										density="compact"
										label="Search for an address"
										class="address-lookup-autocomplete"
										clearable
										:location-bias="userLocation"
										@place-changed="handleAutocompleteSelect" />
								</template>
								<template v-else>
									<v-text-field
										data-test-id="address-search-input"
										label="Search for an address"
										clearable
										:loading="predictions.isLoading.value || predictions.isGeocodingManual.value"
										:model-value="predictions.searchTerm.value"
										@update:model-value="onTouchSearchInput"
										@blur="onTouchSearchBlur" />
								</template>
							</v-col>
						</v-row>
						<v-row v-if="userLocation && showNearbyProjects" no-gutters>
							<v-col>
								<NearbyProjects
									:user-location="userLocation"
									:search-radius="searchRadius"
									@project-selected="selectProject" />
							</v-col>
						</v-row>
						<!-- Nearby Addresses Section -->
						<v-row v-if="isTouchInput && showTouchPredictions && predictions.searchTerm.value" no-gutters>
							<v-col>
								<div class="address-lookup-results-list">
									<v-list density="compact">
										<v-list-item v-if="predictions.isLoading.value" class="pl-2">
											<v-list-item-title class="text-medium-emphasis">Loading addresses...</v-list-item-title>
										</v-list-item>
										<template v-else-if="predictions.items.value.length > 0 || predictions.searchTerm.value.trim()">
											<v-list-item
												v-for="(item, index) in predictions.items.value"
												:key="item.placeId || item.formattedAddress || index"
												data-test-id="address-prediction-item"
												density="compact"
												class="pa-1"
												@mousedown.prevent
												@click="selectPrediction(item)">
												<v-list-item-title>{{ item.formattedAddress }}</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-if="predictions.searchTerm.value.trim()"
												data-test-id="address-create-new"
												class="pa-1"
												@mousedown.prevent
												@click="createManualAddress(predictions.searchTerm.value)">
												<v-list-item-title class="text-truncate">
													Create new address '{{ predictions.searchTerm.value }}'
												</v-list-item-title>
											</v-list-item>
										</template>
										<v-list-item v-else class="pl-2">
											<v-list-item-title>No addresses found.</v-list-item-title>
										</v-list-item>
									</v-list>
								</div>
							</v-col>
						</v-row>
						<v-row v-else-if="googleAddresses.length > 0" no-gutters>
							<v-col>
								<p class="text-h6 mb-0">
									{{ createNewProject ? "Create Project" : "Select An Address" }}
								</p>
								<div class="address-lookup-results-list">
									<v-list density="compact">
										<v-list-item
											v-for="(address, index) in googleAddresses"
											:key="address.place_id || index"
											density="compact"
											:class="[{ 'v-list-item--active': index === selectedAddressIndex }, 'pa-1']"
											@click="selectGoogleAddress(index)"
											@mouseenter="highlightAddress(index)"
											@mouseleave="highlightAddress(null)">
											<template #prepend>
												<v-chip size="small" density="compact" color="primary" class="mr-2 full-width-chip">
													{{ address.distance }}
												</v-chip>
											</template>

											<div class="d-flex align-center">
												<v-tooltip
													:text="address.formatted_address"
													:disabled="!address.formatted_address"
													location="bottom">
													<template #activator="{ props: tooltipProps }">
														<v-list-item-subtitle v-bind="tooltipProps" :aria-label="address.formatted_address">
															{{ address.formatted_address }}
														</v-list-item-subtitle>
													</template>
												</v-tooltip>
											</div>
										</v-list-item>
									</v-list>
								</div>
							</v-col>
						</v-row>
						<!-- Load More Button -->
						<v-row v-if="hasGeocodingAddresses && !hasLoadedGrid" no-gutters>
							<v-col cols="12" class="mt-2">
								<v-btn
									:loading="isLoading"
									color="primary"
									rounded="xl"
									elevation="0"
									variant="outlined"
									block
									@click="loadMoreAddresses">
									Load More Addresses
								</v-btn>
							</v-col>
						</v-row>
					</v-container>
				</div>
			</v-col>
		</v-row>
	</v-sheet>
</template>

<script setup lang="ts">
	/* global google, PermissionName, PermissionState */
	import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick, shallowRef } from "vue";
	import { useRoute } from "vue-router";
	import api from "@/api";
	import DeviceUtility from "@/helpers/device";
	import PermissionHelp from "@/components/location/PermissionHelp.vue";
	import GoogleMapsAutocomplete from "@/components/formfields/GoogleMapsAutocomplete.vue";
	import NearbyProjects from "@/components/location/NearbyProjects.vue";
	import { useGooglePlacePredictions } from "@/composables/useGooglePlacePredictions";
	import { calculateTimeout } from "@/helpers/utilities";
	import { logInfo } from "@/helpers/logError";
	import {
		UserLocation,
		Project,
		calculateDistance,
		GoogleMapsPlace,
		GeoAddress,
		getCurrentLocation as fetchCurrentLocation
	} from "@/helpers/location";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import { useOrganization } from "@/composables/useOrganization";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { validateFormOrWarn } from "@/helpers/Validation";

	const AUTOCOMPLETE_ADDRESS_SOURCE = "autocomplete";
	const GEOCODING_ADDRESS_SOURCE = "geocoding";

	interface FormData {
		name: string;
		address: string;
		latitude: number;
		longitude: number;
	}

	// Vuetify's real VForm#validate() resolves an OBJECT ({ valid, errors }), never a bare boolean —
	// this type was wrong (LSP-16855) and let `if (!validationResult) return;` in submitForm below
	// treat that always-truthy object as "valid", so the guard never fired and an invalid form
	// submitted regardless. See submitForm's `.valid` check.
	interface VuetifyForm {
		validate: () => Promise<{ valid: boolean; errors: Array<{ id: unknown; errorMessages: string[] }> }>;
		reset: () => void;
	}

	type MarkerLike = google.maps.marker.AdvancedMarkerElement | google.maps.Circle;
	type PlaceInput = GoogleMapsPlace | google.maps.GeocoderResult;

	const getGoogleMaps = (): typeof google | undefined => {
		if (typeof window === "undefined") return undefined;
		return (window as typeof window & { google?: typeof google }).google;
	};

	const waitForGoogleMaps = (timeoutMs = 10000, intervalMs = 100): Promise<typeof google> => {
		return new Promise((resolve, reject) => {
			const startTime = Date.now();
			const check = () => {
				const googleMaps = getGoogleMaps();
				if (googleMaps) {
					resolve(googleMaps);
				} else if (Date.now() - startTime > timeoutMs) {
					reject(new Error("Google Maps failed to load within timeout"));
				} else {
					setTimeout(check, intervalMs);
				}
			};
			check();
		});
	};

	const props = withDefaults(
		defineProps<{
			maxResults?: number;
			searchRadius?: number;
			showNearbyProjects?: boolean;
			createNewProject?: boolean;
			initialGeoAddress?: GeoAddress | null;
		}>(),
		{
			maxResults: 5,
			searchRadius: 1000,
			showNearbyProjects: true,
			createNewProject: true,
			initialGeoAddress: null
		}
	);

	const emit = defineEmits<{
		(e: "close"): void;
		// `isNewProject` distinguishes the two ways a project leaves this picker: picking an
		// existing nearby one, or creating one at the current location. Consumers that only need
		// the project (the Action* cards) ignore the second argument.
		(e: "projectSelected", project: Project, isNewProject: boolean): void;
		(e: "geoAddressSelected", geoAddress: GeoAddress): void;
		(e: "locationChanged", location: UserLocation): void;
		(e: "addressSelected", address: GoogleMapsPlace): void;
	}>();
	const route = useRoute();
	const { mobile, height, width } = useDisplay({ mobileBreakpoint: "md" });
	const { currentOrganizationDefaultCountry } = useOrganization();

	const googleAddresses = ref<GoogleMapsPlace[]>([]);
	const currentGoogleLocationAddress = ref<GoogleMapsPlace | null>(null);
	const isValid = ref(false);
	const selectedAddressIndex = ref(0);
	const highlightedIndex = ref<number | null>(null);
	const userLocation = ref<UserLocation | null>(null);
	const isLoading = ref(false);
	const isLoadingLocation = ref(true);
	// Distinct from isLoadingLocation (which starts true to show the mount spinner): this tracks an
	// actually-executing geolocation request so EVERY entry point (onMounted, handleTryAgain,
	// refreshLocation) is guarded against launching a second concurrent fetch (LSP-16468).
	const isRequestInFlight = ref(false);
	// "This is taking longer than expected" + Cancel, revealed after 3.5s of waiting. Driven by a JS
	// timer (CSS animation delays were unreliable on iOS WebKit), so it appears on every browser and
	// is deterministically testable.
	const showSlowEscape = ref(false);
	let slowEscapeTimer: ReturnType<typeof setTimeout> | null = null;
	// True while re-fetching location for an already-loaded map (the refresh button). Distinct from
	// isLoadingLocation so a refresh keeps the current map visible and just spins the button instead
	// of flashing the whole "Getting your location" screen.
	const isRefreshing = ref(false);
	// Identifies the current geolocation flow so a result that arrives after Cancel is ignored.
	let activeLocationRequestId = 0;

	function clearLoadingTimers() {
		if (slowEscapeTimer) clearTimeout(slowEscapeTimer);
		slowEscapeTimer = null;
	}

	// break-it round 2: submitForm's awaited validate() can span a teardown of this component (or of
	// just the Create Project v-form, which is conditionally rendered — see clearForm()). Track mount
	// state the same way IncompleteUploadBanner.vue does, so submitForm can bail instead of resuming
	// into a component that is gone.
	let isComponentMounted = true;
	onUnmounted(() => {
		isComponentMounted = false;
	});

	function cancelLocationRequest(closeAfterCancel = false) {
		activeLocationRequestId++;
		isLoadingLocation.value = false;
		isRefreshing.value = false;
		isRequestInFlight.value = false;
		clearLoadingTimers();
		showSlowEscape.value = false;
		if (closeAfterCancel) goBack();
	}

	watch(
		[isLoadingLocation, isRefreshing],
		([loading, refreshing]) => {
			clearLoadingTimers();
			showSlowEscape.value = false;
			if (loading || refreshing) {
				slowEscapeTimer = setTimeout(() => (showSlowEscape.value = true), 3500);
			}
		},
		{ immediate: true }
	);

	onUnmounted(clearLoadingTimers);

	const isCreatingProject = ref(false);
	const error = ref("");
	const showPermissionHelp = ref(false);
	const map = shallowRef<google.maps.Map | null>(null);
	const isMapInitializing = ref(false);
	const useGeolocationCenter = ref(false);
	const markers = ref<MarkerLike[]>([]);
	const searchCircle = ref<google.maps.Circle | null>(null);
	const geocodingCallCount = ref(0);
	const hasLoadedGrid = ref(false);
	const gridSearchPoints = ref<UserLocation[]>([]);
	const geoAddress = ref<GeoAddress | null>(props.initialGeoAddress ?? null);
	const formData = reactive<FormData>({
		name: "",
		address: "",
		latitude: 0,
		longitude: 0
	});
	// LSP-17482: contact-api's createProject/updateProject now count CODE POINTS for name (projects.ts),
	// matching CharacterLimitedTextField's own :max-length rule, so the standalone server-unit backstop
	// that used to live here is redundant and has been removed.
	//
	// Review finding: both `formData.name` (below, template) and `formData.address` bound plain v-model
	// while CharacterLimitedTextField counts/validates the TRIMMED value by default — CreateProjectDialog
	// already uses v-model.trim for this same name field. Whitespace padding near the limit could look
	// in-budget on the trimmed counter while the RAW value still overflowed the column, or — for
	// address — still overflowed GeoAddress.formattedAddress's varchar(255) column even though no
	// application-level rule catches it (see addressRules' comment below). Both fields now use
	// v-model.trim in the template.
	const nameRules: Array<(value: string) => boolean | string> = [(value) => (value || "").trim().length > 0 || "Name is required."];
	// No server-unit backstop RULE needed for address (§3.3): this value lands on
	// GeoAddress.formattedAddress (db/models/geoAddress.ts), a bare `DataTypes.STRING` — Postgres
	// varchar(255) — with no raw-`.length` application check anywhere in saveProjectAddress/
	// createGeoAddress (projects.ts, controllers/geoAddress.ts). Postgres varchar(n) counts CODE POINTS
	// (§1 of the sweep plan), so the server limit is already the same 255-code-point cap this field's own
	// :max-length enforces — an exact match, not a looser server unit, so there is nothing for a backstop
	// RULE to catch. The v-model.trim above still matters here: an untrimmed value could exceed 255 code
	// points (and so the column) while the trimmed counter showed comfortably under budget.
	const addressRules: Array<(value: string) => boolean | string> = [(value) => (value || "").trim().length > 0 || "Address is required."];
	const fieldErrors = reactive<{ name: string[]; address: string[] }>({
		name: [],
		address: []
	});
	const mapElement = ref<HTMLElement | null>(null);
	const form = ref<VuetifyForm | null>(null);

	const debugMode = computed(() => route.query.debug === "true");
	const selectedAddress = computed<GoogleMapsPlace | null>(() => {
		return googleAddresses.value.length > selectedAddressIndex.value ? googleAddresses.value[selectedAddressIndex.value] : null;
	});
	const initialGeoAddressValue = computed<GeoAddress | null>(() => props.initialGeoAddress ?? null);

	/**
	 * LSP-18456: the project's stored coordinates, coerced to finite numbers.
	 *
	 * `GeoAddress.latitude/longitude` are `DECIMAL(8,6)`/`DECIMAL(9,6)` columns. Postgres returns
	 * `numeric` as a STRING to preserve precision and Sequelize passes it straight through, so what
	 * reaches the browser is `"54.515700"` — despite the model's TypeScript declaring `number`.
	 *
	 * Google Maps is inconsistent about that: `new maps.LatLng("54.5", "13.6")` coerces happily, which
	 * is why the geocoded address list populated fine, but `setCenter({ lat: "54.5" })` is strictly
	 * type-checked and throws `InvalidValueError: not a LatLng ... in property lat: not a number`.
	 * With a `mapId` set, Maps v3.66 renders through WebGL, and that renderer never comes up after the
	 * bad centre — leaving the `.gm-style` wrapper with no <canvas> in it. That was the grey map: not
	 * a sizing problem, not a missing element, a type problem. The refresh button appeared to fix it
	 * only because that path re-centres on `userLocation`, which comes from the browser's Geolocation
	 * API as real numbers.
	 *
	 * Returns null rather than NaN when a coordinate is unusable, so callers can skip centring instead
	 * of handing Maps another invalid value.
	 */
	/** The project centre in `UserLocation` shape, for the grid search that expects that field naming. */
	const initialGeoAddressCenterAsUserLocation = computed<UserLocation | null>(() =>
		initialGeoAddressCenter.value ? { latitude: initialGeoAddressCenter.value.lat, longitude: initialGeoAddressCenter.value.lng } : null
	);

	const initialGeoAddressCenter = computed<{ lat: number; lng: number } | null>(() => {
		const address = initialGeoAddressValue.value;
		if (!address) return null;
		const lat = Number(address.latitude);
		const lng = Number(address.longitude);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			console.warn("AddressLookup: project address has unusable coordinates — not centering on it", {
				latitude: address.latitude,
				longitude: address.longitude
			});
			return null;
		}
		return { lat, lng };
	});
	const isLandscape = computed(() => height.value < width.value);
	const isMobile = computed(() => mobile.value);
	const isMobileLandscape = computed(() => isMobile.value && isLandscape.value);
	const isTouchInput = computed(() => DeviceUtility.isMobile || DeviceUtility.isTablet);
	const mapContainerStyle = computed(() => (isMobileLandscape.value ? { maxHeight: "40vh" } : undefined));
	const hasGeocodingAddresses = computed(() => googleAddresses.value.some((address) => address.source === GEOCODING_ADDRESS_SOURCE));
	const createNewProjectValue = computed(() => props.createNewProject);
	const showNearbyProjectsValue = computed(() => props.showNearbyProjects);
	const searchRadiusValue = computed(() => props.searchRadius);

	const removeAutocompleteAddress = () => {
		googleAddresses.value = googleAddresses.value.filter((address) => address.source !== AUTOCOMPLETE_ADDRESS_SOURCE);
	};

	const centerMap = () => {
		if (!map.value) {
			return;
		}

		if (initialGeoAddressCenter.value && !useGeolocationCenter.value) {
			map.value.setCenter(initialGeoAddressCenter.value);
		} else if (currentGoogleLocationAddress.value) {
			map.value.setCenter({
				lat: currentGoogleLocationAddress.value.geometry.location!.lat(),
				lng: currentGoogleLocationAddress.value.geometry.location!.lng()
			});
		} else if (userLocation.value) {
			map.value.setCenter({
				lat: userLocation.value.latitude,
				lng: userLocation.value.longitude
			});
		}
	};

	const updateFormData = (googleAddress: GoogleMapsPlace) => {
		const latitude = googleAddress.geometry.location!.lat();
		const longitude = googleAddress.geometry.location!.lng();

		formData.address = googleAddress.formatted_address;
		formData.name = googleAddress.formatted_address;
		formData.latitude = latitude;
		formData.longitude = longitude;

		if (isTouchInput.value && createNewProjectValue.value) {
			predictions.searchTerm.value = googleAddress.formatted_address;
			predictions.modelValue.value = {
				formattedAddress: googleAddress.formatted_address,
				latitude,
				longitude,
				placeId: googleAddress.place_id || "",
				distance: googleAddress.distance ?? ""
			};
			predictions.userEditedText.value = false;
		}
	};

	const generateSearchGrid = (center: UserLocation, spacing: number): UserLocation[] => {
		const points: UserLocation[] = [];

		for (let i = -1; i <= 1; i += 1) {
			for (let j = -1; j <= 1; j += 1) {
				points.push({
					latitude: center.latitude + i * spacing,
					longitude: center.longitude + j * spacing
				});
			}
		}

		return points;
	};

	const updateMapMarkers = () => {
		const googleMaps = getGoogleMaps();
		if (!map.value || !googleMaps) {
			return;
		}

		markers.value.forEach((marker: MarkerLike) => {
			if ("setMap" in marker && typeof marker.setMap === "function") {
				marker.setMap(null);
			} else if ("map" in marker) {
				(marker as google.maps.marker.AdvancedMarkerElement).map = null;
			}
		});
		markers.value = [];

		if (searchCircle.value) {
			searchCircle.value.setMap(null);
			searchCircle.value = null;
		}

		if (userLocation.value) {
			const currentLocationMarker = new googleMaps.maps.Circle({
				center: {
					lat: userLocation.value.latitude,
					lng: userLocation.value.longitude
				},
				radius: 5,
				map: map.value,
				strokeColor: "#4285F4",
				strokeOpacity: 1,
				strokeWeight: 3,
				fillColor: "#4285F4",
				fillOpacity: 0.3,
				clickable: true,
				zIndex: 1000
			});

			const innerCircle = new googleMaps.maps.Circle({
				center: {
					lat: userLocation.value.latitude,
					lng: userLocation.value.longitude
				},
				radius: 2,
				map: map.value,
				strokeColor: "#FFFFFF",
				strokeOpacity: 1,
				strokeWeight: 2,
				fillColor: "#4285F4",
				fillOpacity: 1,
				clickable: true,
				zIndex: 1001
			});

			const selectCurrentLocation = () => {
				selectedAddressIndex.value = -1;
				if (currentGoogleLocationAddress.value) {
					emit("addressSelected", currentGoogleLocationAddress.value);
				}
				updateMapMarkers();
			};

			currentLocationMarker.addListener("click", selectCurrentLocation);
			innerCircle.addListener("click", selectCurrentLocation);

			markers.value.push(currentLocationMarker);
			markers.value.push(innerCircle);
		}

		googleAddresses.value.forEach((address, index) => {
			try {
				const pinElement = new googleMaps.maps.marker.PinElement({
					background: index === selectedAddressIndex.value ? "#EA4335" : "#FBBC04",
					borderColor: index === selectedAddressIndex.value ? "#C5221F" : "#F9AB00",
					glyphColor: "#FFFFFF"
				});

				const marker = new googleMaps.maps.marker.AdvancedMarkerElement({
					position: {
						lat: address.geometry.location!.lat(),
						lng: address.geometry.location!.lng()
					},
					map: map.value,
					title: address.name || address.formatted_address,
					content: pinElement.element
				});

				marker.addListener("click", () => {
					selectGoogleAddress(index);
				});

				markers.value.push(marker);
			} catch (markerError) {
				console.error("Error creating AdvancedMarkerElement:", markerError);
			}
		});

		if (hasLoadedGrid.value && gridSearchPoints.value.length > 0 && debugMode.value) {
			gridSearchPoints.value.forEach((point: UserLocation, index) => {
				if (
					userLocation.value &&
					Math.abs(point.latitude - userLocation.value.latitude) < 0.00001 &&
					Math.abs(point.longitude - userLocation.value.longitude) < 0.00001
				) {
					return;
				}

				const magnifyingGlass = document.createElement("div");
				magnifyingGlass.innerHTML =
					'<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>';
				magnifyingGlass.style.backgroundColor = "white";
				magnifyingGlass.style.borderRadius = "50%";
				magnifyingGlass.style.padding = "4px";
				magnifyingGlass.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";

				const gridMarker = new googleMaps.maps.marker.AdvancedMarkerElement({
					position: {
						lat: point.latitude,
						lng: point.longitude
					},
					map: map.value,
					title: `Grid Search Point ${index}`,
					content: magnifyingGlass,
					zIndex: 500
				});

				markers.value.push(gridMarker);
			});
		}

		if (userLocation.value) {
			const radiusMeters = 30;

			searchCircle.value = new googleMaps.maps.Circle({
				strokeColor: "#FF6B35",
				strokeOpacity: 0.8,
				strokeWeight: 2,
				fillColor: "#FF6B35",
				fillOpacity: 0.15,
				map: map.value,
				center: { lat: userLocation.value.latitude, lng: userLocation.value.longitude },
				radius: radiusMeters,
				clickable: false
			});
		}
	};

	// Sets address.distance from the user's location in the org's measurement system.
	// Shared by the initial processing loop and the currentOrganizationDefaultCountry
	// watcher below so both derive the label the same way (LSP-16755).
	const assignAddressDistance = (address: GoogleMapsPlace) => {
		const location = address.geometry?.location;
		if (!userLocation.value || !location) return;
		address.distance = calculateDistance(
			userLocation.value,
			{
				latitude: location.lat(),
				longitude: location.lng()
			},
			currentOrganizationDefaultCountry.value
		);
	};

	const processPlaceResults = async (places: PlaceInput[]) => {
		const processedAddresses: GoogleMapsPlace[] = [];

		for (const place of places) {
			const geometry = (place as GoogleMapsPlace).geometry ?? (place as google.maps.GeocoderResult).geometry;
			if (!geometry?.location) {
				continue;
			}

			let businessName = (place as GoogleMapsPlace).name ?? (place as google.maps.GeocoderResult).name ?? "";
			const types = (place as GoogleMapsPlace).types ?? (place as google.maps.GeocoderResult).types ?? ([] as string[]);

			if (
				!businessName &&
				types.some((type: string) => ["establishment", "point_of_interest", "store", "restaurant"].includes(type))
			) {
				const nameComponent = (place as google.maps.GeocoderResult).address_components?.find(
					(component: google.maps.GeocoderAddressComponent) =>
						component.types.includes("establishment") || component.types.includes("point_of_interest")
				);
				if (nameComponent) {
					businessName = nameComponent.long_name;
				}
			}

			const formattedAddress =
				(place as GoogleMapsPlace).formatted_address ??
				(place as google.maps.GeocoderResult).formatted_address ??
				(place as google.maps.GeocoderResult).vicinity ??
				businessName ??
				"Unknown Address";

			const address: GoogleMapsPlace = {
				place_id: (place as GoogleMapsPlace).place_id ?? (place as google.maps.GeocoderResult).place_id ?? "",
				formatted_address: formattedAddress,
				geometry,
				name: businessName,
				rating: (place as GoogleMapsPlace).rating ?? null,
				types,
				source: GEOCODING_ADDRESS_SOURCE
			};

			assignAddressDistance(address);

			processedAddresses.push(address);
		}

		processedAddresses.sort((a, b) => {
			const aDistance = parseFloat(a.distance?.replace(/[^\d.]/g, "") || "0");
			const bDistance = parseFloat(b.distance?.replace(/[^\d.]/g, "") || "0");
			return aDistance - bDistance;
		});

		googleAddresses.value = processedAddresses;
		selectedAddressIndex.value = 0;
		isLoading.value = false;

		if (processedAddresses.length > 0) {
			emit("addressSelected", processedAddresses[0]);
			updateMapMarkers();
		}
	};

	const initializeMap = async () => {
		if (map.value || isMapInitializing.value) return;
		isMapInitializing.value = true;

		if (!userLocation.value && !initialGeoAddressValue.value) {
			console.log("No user location, skipping map initialization");
			isMapInitializing.value = false;
			return;
		}

		let googleMaps: typeof google;
		try {
			googleMaps = await waitForGoogleMaps();
		} catch (e) {
			console.error("Google Maps failed to load:", e);
			error.value = "Google Maps failed to load. Please refresh the page.";
			isLoading.value = false;
			isMapInitializing.value = false;
			return;
		}

		if (!mapElement.value) {
			await nextTick();
			if (!mapElement.value) {
				console.error("Map element still not available");
				isMapInitializing.value = false;
				return;
			}
		}

		try {
			if (!googleMaps.maps.marker) {
				await googleMaps.maps.importLibrary("marker");
			}

			let initialCenter: { lat: number; lng: number };
			if (useGeolocationCenter.value && userLocation.value) {
				initialCenter = { lat: userLocation.value.latitude, lng: userLocation.value.longitude };
			} else if (initialGeoAddressCenter.value) {
				initialCenter = initialGeoAddressCenter.value;
			} else if (userLocation.value) {
				initialCenter = { lat: userLocation.value.latitude, lng: userLocation.value.longitude };
			} else {
				// LSP-18456: this bailed with only a console line, leaving `isLoading` set and no
				// error on screen — the user sees an empty dialog and no reason for it, the same
				// dead end this ticket is about reached by a different route. Settle the UI.
				console.error("AddressLookup.initializeMap: no location available to center the map", {
					hasUserLocation: !!userLocation.value,
					hasInitialGeoAddress: !!initialGeoAddressValue.value,
					useGeolocationCenter: useGeolocationCenter.value
				});
				error.value = "We couldn't work out where to center the map. Try searching for the address instead.";
				isLoading.value = false;
				isLoadingLocation.value = false;
				isMapInitializing.value = false;
				return;
			}

			map.value = new googleMaps.maps.Map(mapElement.value, {
				center: initialCenter,
				zoom: 18,
				mapId: "LOCATION_MAP",
				disableDefaultUI: true,
				zoomControl: false,
				gestureHandling: "greedy",
				fullscreenControl: false,
				streetViewControl: false,
				mapTypeControl: false
			});

			if (userLocation.value) {
				searchNearbyPlaces();
			} else if (initialGeoAddressCenter.value) {
				// LSP-18456: this is the "opened the pin on a project that already has an address"
				// path. `onMounted` short-circuits before requesting a location here, so there is no
				// userLocation and this branch used to just clear the spinner — leaving the results
				// list permanently empty with nothing in flight to fill it. Search around the address
				// we were handed instead; it is the address the user is standing on in the UI.
				searchNearbyPlaces({
					latitude: initialGeoAddressCenter.value.lat,
					longitude: initialGeoAddressCenter.value.lng
				});
			} else {
				isLoading.value = false;
				updateMapMarkers();
			}
		} catch (mapError) {
			console.error("Error initializing map:", mapError);
			error.value = `Failed to initialize Google Maps: ${mapError instanceof Error ? mapError.message : "Unknown error"}`;
			isLoading.value = false;
		} finally {
			isMapInitializing.value = false;
		}
	};

	const goBack = () => {
		emit("close");
	};

	const selectProject = (project: Project) => {
		emit("projectSelected", project, false);
	};

	const highlightAddress = (index: number | null) => {
		highlightedIndex.value = index;
	};

	function selectGoogleAddress(index: number): void {
		const selected = googleAddresses.value[index];
		if (!selected) {
			return;
		}

		currentGoogleLocationAddress.value = selected;
		selectedAddressIndex.value = index;

		if (!createNewProjectValue.value) {
			const geoAddress: GeoAddress = {
				formattedAddress: selected.formatted_address,
				latitude: selected.geometry.location!.lat(),
				longitude: selected.geometry.location!.lng(),
				placeId: selected.place_id || "",
				distance: selected.distance ?? ""
			};
			emit("geoAddressSelected", geoAddress);
			return;
		}

		updateFormData(selected);
		updateMapMarkers();
		centerMap();
	}

	const handleAutocompleteSelect = (place: GeoAddress | null) => {
		if (!place || !place.placeId || !place.formattedAddress || place.latitude == null || place.longitude == null) {
			googleAddresses.value = [];
			return;
		}

		const googleAddress: GoogleMapsPlace = {
			place_id: place.placeId,
			formatted_address: place.formattedAddress || "",
			geometry: {
				location: {
					lat: () => place.latitude,
					lng: () => place.longitude
				}
			},
			name: place.formattedAddress || "",
			rating: null,
			source: AUTOCOMPLETE_ADDRESS_SOURCE
		};

		const isTouchCreateProjectReselection =
			isTouchInput.value && createNewProjectValue.value && currentGoogleLocationAddress.value !== null;
		if (isTouchCreateProjectReselection) {
			removeAutocompleteAddress();
		}

		googleAddresses.value = [...googleAddresses.value, googleAddress];
		selectGoogleAddress(googleAddresses.value.length - 1);
	};

	const predictions = useGooglePlacePredictions({
		locationBias: userLocation,
		onPlaceChanged: handleAutocompleteSelect
	});
	if (initialGeoAddressValue.value) {
		predictions.modelValue.value = initialGeoAddressValue.value;
		predictions.setSearchTermSilent(initialGeoAddressValue.value.formattedAddress);
	}

	// On touch, the inline predictions list is open ONLY while the user is actively typing.
	// Selecting/creating an address (or blurring) closes it, so taps land on the field — letting
	// the user delete/retype to get fresh suggestions instead of re-tapping a stale list (LSP-16445).
	const showTouchPredictions = ref(false);
	const pendingTouchSearchCommit = ref<Promise<void> | null>(null);
	const touchPredictionsListRef = ref<HTMLElement | null>(null);

	// On iPad/iPhone the on-screen keyboard does not reflow the document, so the predictions list
	// rendered below the address field on the Create Project form can land below the visible
	// viewport — only a sliver of the first suggestion shows. Scroll the first item into view as
	// soon as predictions arrive so the user sees the full top suggestion without having to scroll.
	watch(
		() => [showTouchPredictions.value, predictions.items.value.length, predictions.isLoading.value] as const,
		([showing, itemCount, loading]) => {
			if (!showing || loading || itemCount === 0) return;
			nextTick(() => {
				const firstItem = touchPredictionsListRef.value?.querySelector<HTMLElement>('[data-test-id="address-prediction-item"]');
				firstItem?.scrollIntoView({ block: "nearest" });
			});
		}
	);

	const onTouchSearchInput = (text: string | undefined) => {
		showTouchPredictions.value = true;
		predictions.onSearchInput(text);
	};

	const trackTouchSearchCommit = (commitPromise: Promise<void>): Promise<void> => {
		let trackedCommit: Promise<void>;
		trackedCommit = commitPromise
			.catch((unknownError) => {
				console.error("Error committing touch address:", unknownError);
			})
			.finally(() => {
				if (pendingTouchSearchCommit.value === trackedCommit) {
					pendingTouchSearchCommit.value = null;
				}
			});

		pendingTouchSearchCommit.value = trackedCommit;
		return trackedCommit;
	};

	const onTouchSearchBlur = () => {
		showTouchPredictions.value = false;
		trackTouchSearchCommit(predictions.handleBlur());
	};

	const selectPrediction = async (geoAddress: Partial<GeoAddress>) => {
		showTouchPredictions.value = false;
		await predictions.selectPrediction(geoAddress as GeoAddress);
	};

	const createManualAddress = async (text?: string) => {
		showTouchPredictions.value = false;
		await predictions.createManualAddress(text);
	};

	const commitTouchProjectAddressIfNeeded = async () => {
		if (pendingTouchSearchCommit.value) {
			await pendingTouchSearchCommit.value;
		}

		if (!isTouchInput.value || !createNewProjectValue.value || !currentGoogleLocationAddress.value) {
			return;
		}

		if (!predictions.userEditedText.value) {
			return;
		}

		await createManualAddress(predictions.searchTerm.value);
	};

	const refreshLocation = () => {
		// Refresh in place: keep the current map visible and re-center it when the new location
		// arrives (the userLocation watcher does this). We pass refresh=true so requestCurrentLocation
		// spins the refresh button instead of swapping to the full "Getting your location" screen.
		useGeolocationCenter.value = true;
		requestCurrentLocation(false, true);
	};

	const handleTryAgain = async () => {
		if (isLoadingLocation.value) return;
		// requestCurrentLocation(true) fully handles the retry: it keeps the denied card visible while
		// re-checking, stays on it if still denied, or clears it and shows results on success. The old
		// manual reset + second requestCurrentLocation() call here caused PermissionHelp to flash
		// off then back on (LSP-15972 Try Again flicker), so it is intentionally gone.
		await requestCurrentLocation(true);
	};

	const submitForm = async () => {
		if (isCreatingProject.value) {
			return;
		}

		// Set this before the first await so a second click cannot enter while address commit or validation
		// is pending. The finally below also releases the control for each early validation return.
		isCreatingProject.value = true;

		try {
			fieldErrors.name = [];
			fieldErrors.address = [];
			await commitTouchProjectAddressIfNeeded();

			// LSP-16855: the v-form ref is only populated while the Create Project block is rendered, so a
			// stray submitForm() call (e.g. a keyup.enter racing an unmount) can find it absent.
			const validationResult = await validateFormOrWarn(
				form.value,
				"[AddressLookup] submitForm: form ref not mounted, cannot submit"
			);

			// The Create Project form (or the whole component) can unmount while validation is pending — e.g.
			// clearForm() clearing currentGoogleLocationAddress, which v-if's the form away. Resuming would
			// create a project from cleared/stale formData, so bail before using the result.
			if (!isComponentMounted || !form.value || !validationResult) {
				if (!isComponentMounted || !form.value) {
					console.warn("[AddressLookup] submitForm: component or form unmounted while validate() was pending, not submitting");
				}
				return;
			}

			// Vuetify's validate() resolves an object, not a boolean (see VuetifyForm above) — check `.valid` explicitly.
			if (!validationResult.valid) {
				console.warn("[AddressLookup] submitForm: form invalid, not submitting", {
					errors: (validationResult.errors ?? []).map((error) => error.errorMessages)
				});
				return;
			}

			const project = {
				name: formData.name,
				GeoAddress: {
					formattedAddress: formData.address,
					latitude: formData.latitude,
					longitude: formData.longitude,
					placeId: selectedAddress.value?.place_id ?? ""
				}
			};

			const newProject = await api.createProject(project);
			clearForm();
			emit("projectSelected", newProject, true);
		} catch (unknownError) {
			const possibleError = unknownError as { status?: string; message?: string } | undefined;
			if (possibleError?.status === "error" && possibleError.message === "Project name already exists.") {
				isValid.value = false;
				fieldErrors.name.push("Project name already exists.");
			} else {
				console.error("Error creating project:", unknownError);
				error.value = "Error creating project. Please try again.";
			}
		} finally {
			isCreatingProject.value = false;
		}
	};

	const clearForm = () => {
		geoAddress.value = null;
		const currentForm = form.value;
		if (currentForm?.reset) {
			currentForm.reset();
		}

		formData.name = "";
		formData.address = "";
		formData.latitude = 0;
		formData.longitude = 0;
		isValid.value = false;
		currentGoogleLocationAddress.value = null;
		fieldErrors.name = [];
		fieldErrors.address = [];

		removeAutocompleteAddress();
		centerMap();
	};

	const selectCurrentLocation = () => {
		selectedAddressIndex.value = -1;
		if (currentGoogleLocationAddress.value) {
			emit("addressSelected", currentGoogleLocationAddress.value);
		}
		updateMapMarkers();
	};

	/**
	 * LSP-18456: takes the centre to search around rather than always reading `userLocation`.
	 *
	 * Opening this dialog from a project that already HAS an address never obtains a user location
	 * (see `onMounted`), so the old unconditional `if (!userLocation.value) return` meant no search
	 * was ever issued and the results list stayed empty — the "nothing is displayed until you press
	 * refresh" report. The project's own address is a perfectly good centre; use it when that is all
	 * we have. Defaults to `userLocation` so every existing caller behaves exactly as before.
	 */
	const searchNearbyPlaces = (center: UserLocation | null = userLocation.value) => {
		if (!center) {
			return;
		}

		const googleMaps = getGoogleMaps();
		if (!googleMaps) {
			error.value = "Google Maps not loaded.";
			isLoading.value = false;
			return;
		}

		isLoading.value = true;
		geocodingCallCount.value = 0;
		hasLoadedGrid.value = false;
		currentGoogleLocationAddress.value = null;

		const geocoder = new googleMaps.maps.Geocoder();
		geocodingCallCount.value += 1;

		const request: google.maps.GeocoderRequest = {
			location: new googleMaps.maps.LatLng(center.latitude, center.longitude)
		};
		// Only "your exact location" when the centre actually IS the user's location — searching
		// around a project's stored address and then blaming the user's location is misleading.
		const searchedOwnLocation = center === userLocation.value;

		geocoder.geocode(request, async (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
			const allAddresses: google.maps.GeocoderResult[] = [];

			if (status === "OK" && results && results.length > 0) {
				const relevantAddresses = results.filter((result: google.maps.GeocoderResult) =>
					[
						"street_address",
						"premise",
						"subpremise",
						"establishment",
						"point_of_interest",
						"store",
						"restaurant",
						"shopping_mall",
						"gas_station",
						"hospital",
						"school",
						"bank",
						"pharmacy"
					].some((type: string) => result.types?.includes(type) ?? false)
				);

				allAddresses.push(...relevantAddresses);
			}

			if (allAddresses.length > 0) {
				await processPlaceResults(allAddresses);
			} else {
				const message = searchedOwnLocation
					? "No residential addresses found at your exact location. Try searching manually."
					: "No residential addresses found near this address. Try searching manually.";
				const toast = useToast();
				toast.warning(message, { timeout: calculateTimeout(message) });
				isLoading.value = false;
			}
		});
	};

	const loadMoreAddresses = async () => {
		// LSP-18456 (Bugbot, MEDIUM): this used to hard-require `userLocation`, which was fine while the
		// results list could only ever be filled from geolocation. It can now be filled by searching
		// around the project's own stored address, and that path never requests a location — so "Load
		// More Addresses" appeared and silently did nothing. Widen the grid centre the same way the
		// initial search was widened.
		const gridCenter = userLocation.value ?? initialGeoAddressCenterAsUserLocation.value;
		if (!gridCenter || hasLoadedGrid.value) {
			return;
		}

		const googleMaps = getGoogleMaps();
		if (!googleMaps) {
			error.value = "Google Maps not loaded.";
			return;
		}

		isLoading.value = true;

		const geocoder = new googleMaps.maps.Geocoder();
		const searchPoints = generateSearchGrid(gridCenter, 0.0003);

		gridSearchPoints.value = searchPoints;

		const geocodePromises = searchPoints
			.filter((point) => {
				return !(
					Math.abs(point.latitude - gridCenter.latitude) < 0.00001 && Math.abs(point.longitude - gridCenter.longitude) < 0.00001
				);
			})
			.map(
				(point: UserLocation, index) =>
					new Promise<google.maps.GeocoderResult[] | null>((resolve) => {
						geocodingCallCount.value += 1;

						geocoder.geocode(
							{ location: new googleMaps.maps.LatLng(point.latitude, point.longitude) },
							(results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
								if (status === "OK" && results && results.length > 0) {
									const relevantAddresses = results.filter((result: google.maps.GeocoderResult) =>
										[
											"street_address",
											"premise",
											"subpremise",
											"establishment",
											"point_of_interest",
											"store",
											"restaurant",
											"shopping_mall",
											"gas_station",
											"hospital",
											"school",
											"bank",
											"pharmacy"
										].some((type: string) => result.types?.includes(type) ?? false)
									);

									console.log(
										`  → Grid point ${index + 1} produced ${relevantAddresses.length} addresses and businesses (from ${
											results.length
										} total results)`
									);
									resolve(relevantAddresses.length > 0 ? relevantAddresses : null);
								} else {
									resolve(null);
								}
							}
						);
					})
			);

		const geocodeResults = await Promise.all(geocodePromises);
		const nearbyAddresses = geocodeResults
			.filter((result): result is google.maps.GeocoderResult[] => Array.isArray(result) && result.length > 0)
			.flat();

		const currentAddresses = googleAddresses.value.map((addr) => ({
			place_id: addr.place_id,
			formatted_address: addr.formatted_address,
			geometry: addr.geometry,
			name: addr.name,
			rating: addr.rating,
			types: addr.types,
			source: addr.source
		}));

		const allAddresses: PlaceInput[] = [...currentAddresses, ...nearbyAddresses];
		const seenAddresses = new Set<string>();

		const uniqueAddresses = allAddresses.filter((address) => {
			const formatted = "formatted_address" in address ? address.formatted_address : "";
			if (!formatted) {
				return false;
			}
			if (seenAddresses.has(formatted)) {
				return false;
			}
			seenAddresses.add(formatted);
			return true;
		});

		hasLoadedGrid.value = true;
		await processPlaceResults(uniqueAddresses);
	};

	const requestCurrentLocation = async (tryAgain = false, refresh = false) => {
		// Single in-flight guard for ALL entry points (onMounted, handleTryAgain, refreshLocation).
		// Without this, a refresh or re-request while a fetch is pending launches a second concurrent
		// geolocation call — the same double-fetch race LSP-16468 guarded for the "Try Again" button.
		if (isRequestInFlight.value) return;
		const myRequestId = activeLocationRequestId;
		isRequestInFlight.value = true;
		if (refresh) {
			// Refresh of an already-loaded map: keep the current map/results visible and just spin the
			// refresh button. Do NOT flip isLoadingLocation (that swaps to the loading screen) or clear
			// the view. On success below we update userLocation and the map re-centers in place.
			isRefreshing.value = true;
		} else if (!tryAgain) {
			// On a retry, keep the currently-shown card (permission help OR a non-permission error
			// message) visible until we have a result — do NOT clear error or flash the loading screen.
			// Only the initial (non-retry) request resets the view.
			isLoadingLocation.value = true;
			error.value = "";
			showPermissionHelp.value = false;
		}

		try {
			let permissionState: PermissionState | "unsupported" = "unsupported";
			try {
				if (navigator.permissions?.query) {
					const status = await navigator.permissions.query({ name: "geolocation" as PermissionName });
					permissionState = status.state;
				}
			} catch (permissionsError) {
				permissionState = "unsupported";
				logInfo("AddressLookup: permissions.query unavailable, skipping pre-flight check", {
					feature: "address-lookup",
					permissionState: "unsupported",
					error: permissionsError instanceof Error ? permissionsError.message : String(permissionsError)
				});
			}
			if (myRequestId !== activeLocationRequestId) return false;

			// On an iOS/iPadOS retry, skip the pre-flight short-circuit so the flow reaches the
			// getCurrentPosition catch below, where a full page reload re-prompts for permission —
			// WebKit will not re-prompt for the page's lifetime otherwise. Use isIosOrIpados (not
			// isIOS) so iPadOS Safari, which sends a desktop UA, behaves the same as iPad Chrome.
			if (permissionState === "denied" && !(DeviceUtility.isIosOrIpados && tryAgain)) {
				showPermissionHelp.value = true;
				// Keep the denial reason consistent with the getCurrentPosition path, whether the
				// browser reports it during pre-flight or at the position request. PermissionHelp
				// suppresses this duplicate sentence while its recovery instructions are visible;
				// retaining the value here also overwrites a stale timeout/unavailable error.
				error.value = "Location access denied. Please enable location permissions and try again.";
				logInfo("AddressLookup: geolocation permission denied before current location request", {
					feature: "address-lookup",
					permissionState: "denied"
				});
				isLoadingLocation.value = false;
				return false;
			}

			const autoRedirect = () => {
				let url = window.location.href;
				const separator = url.includes("?") ? "&" : "?";
				if (!url.includes("goTo=address")) {
					url = `${url}${separator}goTo=address`;
				}
				window.location.href = url;
			};

			try {
				const location = await fetchCurrentLocation();
				if (myRequestId !== activeLocationRequestId) return false;
				// Success — leave the denied card and surface results (matters on a retry, where we
				// intentionally kept PermissionHelp visible until we knew the outcome).
				showPermissionHelp.value = false;
				error.value = "";
				userLocation.value = location;
				emit("locationChanged", location);
				return true;
			} catch (unknownError) {
				if (myRequestId !== activeLocationRequestId) return false;
				const parsedError = unknownError instanceof Error ? unknownError : new Error(String(unknownError ?? "Unknown error"));
				error.value = parsedError.message;

				// iOS/iPadOS only: WebKit will not re-prompt for geolocation once denied for the page's
				// lifetime, so a "Try Again" must reload the page to get a fresh prompt. isIosOrIpados
				// keeps this consistent across iPad Safari (desktop UA) and Chrome on iOS.
				if (DeviceUtility.isIosOrIpados && tryAgain && parsedError.message.includes("denied")) {
					autoRedirect();
					return false;
				}

				if (parsedError.message.includes("denied")) {
					showPermissionHelp.value = true;
					return false;
				}

				// Non-permission failure (timeout / unavailable): show ONLY the error alert. Clear any
				// stale permission-help state so the enable-permission copy does not linger from a
				// prior denied attempt alongside the new error.
				showPermissionHelp.value = false;
				return false;
			} finally {
				if (myRequestId === activeLocationRequestId) isLoadingLocation.value = false;
			}
		} finally {
			if (myRequestId === activeLocationRequestId) {
				isRequestInFlight.value = false;
				isRefreshing.value = false;
			}
		}
	};

	watch(
		userLocation,
		async (newLocation, oldLocation) => {
			if (newLocation === oldLocation) return;
			await nextTick();
			if (map.value && newLocation) {
				// Map already exists (e.g. a refresh) — re-center it in place instead of rebuilding,
				// so the refresh does not blank/rebuild the map. initializeMap no-ops while a map exists.
				map.value.setCenter({ lat: newLocation.latitude, lng: newLocation.longitude });
				searchNearbyPlaces();
				updateMapMarkers();
			} else {
				await initializeMap();
			}
		},
		{ immediate: true }
	);

	// currentOrganizationDefaultCountry loads async (undefined until getMe() resolves).
	// If it resolves/changes after addresses were already processed, recompute their
	// displayed distances in the correct measurement system without re-querying Google.
	// Labels only — order is left untouched so a user's current selection is not disrupted.
	watch(
		() => currentOrganizationDefaultCountry.value,
		() => {
			googleAddresses.value.forEach(assignAddressDistance);
		}
	);

	watch(
		() => formData.name,
		(newVal, oldVal) => {
			if (newVal !== oldVal) {
				fieldErrors.name = [];
			}
		}
	);

	onMounted(() => {
		if (initialGeoAddressValue.value) {
			isLoadingLocation.value = false;
			initializeMap();
			return;
		}

		requestCurrentLocation();
	});

	watch(
		initialGeoAddressValue,
		async (newGeoAddress, oldGeoAddress) => {
			if (!newGeoAddress) return;
			if (
				oldGeoAddress &&
				newGeoAddress.latitude === oldGeoAddress.latitude &&
				newGeoAddress.longitude === oldGeoAddress.longitude &&
				newGeoAddress.formattedAddress === oldGeoAddress.formattedAddress
			) {
				return;
			}

			useGeolocationCenter.value = false;
			if (!map.value) {
				await nextTick();
				await initializeMap();
			}
			centerMap();
		},
		{ immediate: true }
	);
</script>

<style scoped>
	.full-width-chip {
		width: 100%;
		justify-content: center;
	}

	/* Ease the resolved content (PermissionHelp / map / results) in when it replaces the loading
	   state, so it does not hard-pop after the brief empty loading frame (LSP-15972 polish — the
	   "disappear then reappear" feel). Purely cosmetic; no layout/height impact. */
	.address-lookup-content {
		animation: address-lookup-content-in 180ms ease-out;
	}

	@keyframes address-lookup-content-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	.map-btn-back {
		position: absolute;
		top: 10px;
		left: 10px;
	}

	.map-btn-refresh {
		position: absolute;
		bottom: 10px;
		right: 10px;
	}

	.address-lookup-results-column {
		min-height: 0;
	}

	.address-lookup-map-container {
		position: relative;
	}

	.address-lookup-map {
		height: 250px;
		width: 100%;
		background-color: #f0f0f0;
	}

	/* Use dvh (dynamic viewport height) so the list shrinks when the on-screen keyboard opens on
	   iPad/iPhone — vh would size against the layout viewport and leave the list extending behind
	   the keyboard, hiding the top prediction. */
	.address-lookup-results-list {
		overflow-y: auto;
		max-height: min(18rem, calc(100dvh - 22rem));
	}

	@media (orientation: landscape) and (max-height: 600px) {
		.address-lookup-map-container {
			max-height: 40vh;
			overflow: hidden;
		}

		.address-lookup-map {
			height: 40vh;
			max-height: 40vh;
		}

		.address-lookup-results-list {
			max-height: calc(100dvh - 10rem);
		}
	}

	.address-lookup-autocomplete:deep(.v-icon) {
		display: none;
	}
</style>
